/**
 * Gestionnaire d'erreurs API pour le frontend Sphere.
 *
 * ⚠️ **Usage Frontend uniquement** - Pour le backend, utilisez `ServiceErrorHandler` depuis `@sphere/shared/server`.
 *
 * Ce handler est spécialisé pour :
 * - Transformer les erreurs Axios/API en structures d'erreur utilisateur
 * - Extraire les codes d'erreur depuis les réponses HTTP
 * - Gérer les erreurs de validation de champ
 * - Fournir des messages d'erreur localisés
 *
 * @example
 * ```typescript
 * import { handleApiError } from '@sphere/shared/util'
 *
 * try {
 *   await api.post('/users', data)
 * } catch (error) {
 *   const errorResponse = handleApiError(error)
 *   // Afficher errorResponse.general ou errorResponse.fields
 * }
 * ```
 *
 * Les types d'erreurs sont définis localement dans `./types/errors.ts` pour éviter
 * les dépendances vers le frontend.
 */

import type {
  UserError,
  ErrorResponse,
  FieldError,
  ErrorSeverity,
  ErrorCategory,
} from './types/errors'
import { AuthErrorCodes } from './types/errors'

// Messages d'erreur localisés
const ERROR_MESSAGES: Record<
  string,
  {
    message: string
    severity: ErrorSeverity
    category: ErrorCategory
  }
> = {
  // Erreurs de connexion
  [AuthErrorCodes.INVALID_CREDENTIALS]: {
    message: 'Adresse email ou mot de passe incorrect',
    severity: 'error',
    category: 'auth',
  },
  [AuthErrorCodes.USER_NOT_FOUND]: {
    message: 'Aucun compte trouvé avec cette adresse email',
    severity: 'error',
    category: 'auth',
  },
  [AuthErrorCodes.ACCOUNT_LOCKED]: {
    message: 'Compte verrouillé temporairement. Contactez le support',
    severity: 'error',
    category: 'auth',
  },
  [AuthErrorCodes.ACCOUNT_DISABLED]: {
    message: 'Compte désactivé. Contactez le support',
    severity: 'error',
    category: 'auth',
  },
  [AuthErrorCodes.TOO_MANY_ATTEMPTS]: {
    message: 'Trop de tentatives. Réessayez dans quelques minutes',
    severity: 'warning',
    category: 'auth',
  },

  // Erreurs d'inscription
  [AuthErrorCodes.EMAIL_ALREADY_EXISTS]: {
    message: 'Cette adresse email est déjà utilisée',
    severity: 'error',
    category: 'auth',
  },
  [AuthErrorCodes.USERNAME_ALREADY_EXISTS]: {
    message: "Ce nom d'utilisateur est déjà pris",
    severity: 'error',
    category: 'auth',
  },
  [AuthErrorCodes.WEAK_PASSWORD]: {
    message: 'Mot de passe trop faible (min. 8 caractères)',
    severity: 'error',
    category: 'auth',
  },
  [AuthErrorCodes.INVALID_EMAIL_FORMAT]: {
    message: "Format d'adresse email invalide",
    severity: 'error',
    category: 'validation',
  },
  [AuthErrorCodes.TERMS_NOT_ACCEPTED]: {
    message: "Vous devez accepter les conditions d'utilisation",
    severity: 'error',
    category: 'validation',
  },

  // Erreurs générales
  [AuthErrorCodes.NETWORK_ERROR]: {
    message: 'Problème de connexion internet',
    severity: 'error',
    category: 'network',
  },
  [AuthErrorCodes.SERVER_ERROR]: {
    message: 'Erreur du serveur. Réessayez plus tard',
    severity: 'error',
    category: 'server',
  },
  [AuthErrorCodes.UNAUTHORIZED]: {
    message: 'Adresse email ou mot de passe incorrect',
    severity: 'error',
    category: 'auth',
  },
  [AuthErrorCodes.FORBIDDEN]: {
    message: 'Accès refusé',
    severity: 'error',
    category: 'auth',
  },
  [AuthErrorCodes.VALIDATION_ERROR]: {
    message: 'Informations invalides',
    severity: 'error',
    category: 'validation',
  },
  [AuthErrorCodes.UNKNOWN_ERROR]: {
    message: "Une erreur s'est produite. Réessayez",
    severity: 'error',
    category: 'unknown',
  },
}

/**
 * Extrait le code d'erreur à partir d'une erreur API
 */
function extractErrorCode(error: any): string {
  // Vérifier si c'est une erreur Axios
  if (error?.response?.data?.code) {
    return error.response.data.code
  }

  // Vérifier si c'est une erreur avec un message contenant un code
  if (error?.response?.data?.message) {
    const message = error.response.data.message
    // Chercher des patterns courants dans les messages d'erreur
    if (
      message.includes('Invalid credentials') ||
      message.includes('invalid_credentials')
    ) {
      return AuthErrorCodes.INVALID_CREDENTIALS
    }
    if (
      message.includes('User not found') ||
      message.includes('user_not_found')
    ) {
      return AuthErrorCodes.USER_NOT_FOUND
    }
    if (
      message.includes('Email already exists') ||
      message.includes('email_exists')
    ) {
      return AuthErrorCodes.EMAIL_ALREADY_EXISTS
    }
    if (
      message.includes('Username already exists') ||
      message.includes('username_exists')
    ) {
      return AuthErrorCodes.USERNAME_ALREADY_EXISTS
    }
  }

  // Vérifier le code de statut HTTP
  if (error?.response?.status) {
    switch (error.response.status) {
      case 401:
        return AuthErrorCodes.UNAUTHORIZED
      case 403:
        return AuthErrorCodes.FORBIDDEN
      case 422:
        return AuthErrorCodes.VALIDATION_ERROR
      case 500:
        return AuthErrorCodes.SERVER_ERROR
    }
  }

  // Vérifier si c'est une erreur réseau
  if (!error?.response || error.code === 'NETWORK_ERROR') {
    return AuthErrorCodes.NETWORK_ERROR
  }

  return AuthErrorCodes.UNKNOWN_ERROR
}

/**
 * Extrait les erreurs de champ à partir d'une erreur API
 */
function extractFieldErrors(error: any): Record<string, FieldError> {
  const fieldErrors: Record<string, FieldError> = {}

  // Vérifier les erreurs de validation détaillées
  if (error?.response?.data?.errors) {
    const errors = error.response.data.errors

    // Format: { field: 'email', message: 'Email is invalid' }[]
    if (Array.isArray(errors)) {
      errors.forEach((err: any) => {
        if (err.field && err.message) {
          fieldErrors[err.field] = {
            field: err.field,
            code: err.code || 'VALIDATION_ERROR',
            message: err.message,
          }
        }
      })
    }

    // Format: { email: ['Email is invalid'], password: ['Password too short'] }
    else if (typeof errors === 'object') {
      Object.entries(errors).forEach(([field, messages]) => {
        if (Array.isArray(messages) && messages.length > 0) {
          fieldErrors[field] = {
            field,
            code: 'VALIDATION_ERROR',
            message: messages[0],
          }
        }
      })
    }
  }

  return fieldErrors
}

/**
 * Transforme une erreur API (Axios/HTTP) en structure d'erreur utilisateur formatée.
 *
 * Cette fonction analyse les erreurs API et les convertit en structures d'erreur
 * utilisateur avec messages localisés et catégorisation.
 *
 * ⚠️ **Usage Frontend uniquement** - Pour le backend, utilisez `ServiceErrorHandler`.
 *
 * @param error - Erreur API (généralement une erreur Axios)
 * @returns Structure d'erreur avec soit `fields` (erreurs de validation) soit `general` (erreur globale)
 *
 * @example
 * ```typescript
 * import { handleApiError } from '@sphere/shared/util'
 *
 * try {
 *   await api.post('/users', data)
 * } catch (error) {
 *   const errorResponse = handleApiError(error)
 *   if (errorResponse.fields) {
 *     // Afficher les erreurs de champ
 *   } else if (errorResponse.general) {
 *     // Afficher l'erreur générale
 *   }
 * }
 * ```
 */
export function handleApiError(error: any): ErrorResponse {
  const errorCode = extractErrorCode(error)
  const fieldErrors = extractFieldErrors(error)

  // Si nous avons des erreurs de champ, privilégier celles-ci
  if (Object.keys(fieldErrors).length > 0) {
    return {
      fields: fieldErrors,
    }
  }

  // Sinon, retourner l'erreur générale
  const errorConfig =
    ERROR_MESSAGES[errorCode] || ERROR_MESSAGES[AuthErrorCodes.UNKNOWN_ERROR]

  const userError: UserError = {
    id: `error_${Date.now()}`,
    title: '',
    message: errorConfig?.message || 'Une erreur est survenue',
    severity: errorConfig?.severity || 'error',
    category: errorConfig?.category || 'unknown',
    code: errorCode,
  }

  return {
    general: userError,
  }
}

/**
 * Gestionnaire spécialisé pour les erreurs d'authentification.
 *
 * Alias de `handleApiError` pour clarifier l'intention dans le code.
 *
 * ⚠️ **Usage Frontend uniquement** - Pour le backend, utilisez `ServiceErrorHandler`.
 *
 * @param error - Erreur API d'authentification
 * @returns Structure d'erreur formatée pour l'authentification
 */
export function handleAuthError(error: any): ErrorResponse {
  return handleApiError(error)
}

/**
 * Utilitaire pour créer une erreur utilisateur personnalisée.
 *
 * ⚠️ **Usage Frontend uniquement** - Pour le backend, utilisez les exceptions NestJS directement.
 *
 * @param code - Code d'erreur (ex: AuthErrorCodes.INVALID_CREDENTIALS)
 * @param customMessage - Message personnalisé (optionnel, utilise le message par défaut sinon)
 * @param field - Nom du champ si c'est une erreur de validation (optionnel)
 * @returns Structure UserError formatée
 */
export function createUserError(
  code: string,
  customMessage?: string,
  field?: string
): UserError {
  const errorConfig =
    ERROR_MESSAGES[code] || ERROR_MESSAGES[AuthErrorCodes.UNKNOWN_ERROR]

  return {
    id: `error_${Date.now()}`,
    title: '',
    code,
    message: customMessage || errorConfig?.message || 'Erreur de validation',
    severity: errorConfig?.severity || 'error',
    category: errorConfig?.category || 'validation',
    field,
  }
}
